// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '../firebase'
import { useCurrentAdmin } from '@/composables/useCurrentAdmin'

const routes = [
  {
    path: '/login',
    name: 'login',
    meta: { title: 'Amarel: Login' },
    component: () => import('@/views/LoginView.vue')
  },

  // Admin layout (protected)
  {
    path: '/',
    name: 'Home',
    component: () => import('@/layout/AdminLayout/index.vue'),
    meta: { requiresAuth: true, title: 'Amarel: Dashboard' },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue')
      }
    ]
  },

  // Events (protected)
  {
    path: '/event',
    component: () => import('@/layout/AdminLayout/index.vue'),
    meta: { requiresAuth: true, title: 'Amarel: Events' },
    children: [
      {
        path: '',
        name: 'EventList',
        component: () => import('@/views/Events/EventList.vue')
      },
      {
        path: 'new',
        name: 'EventNew',
        component: () => import('@/views/Events/EventNew.vue')
      },
      {
        path: 'detail/:id',
        name: 'EventDetail',
        component: () => import('@/views/Events/EventDetail.vue')
      }
    ]
  },

  // Matching (protected)
  {
    path: '/matching',
    component: () => import('@/layout/AdminLayout/index.vue'),
    meta: { requiresAuth: true, title: 'Amarel: Matching' },
    children: [
      {
        path: '',
        name: 'MatchingResult',
        component: () => import('@/views/Matching/MatchingResult.vue')
      },
      {
        path: 'detail/:eventId/:id',
        name: 'MatchingDetail',
        component: () => import('@/views/Matching/MatchingDetail.vue')
      }
    ]
  },
  // Attendees (protected)
  {
    path: '/attendees',
    component: () => import('@/layout/AdminLayout/index.vue'),
    meta: { requiresAuth: true, title: 'Amarel: Attendees' },
    children: [
      {
        path: '',
        name: 'AttendeesList',
        component: () => import('@/views/Attendees/AttendeesList.vue')
      },
      {
        path: 'detail/:id',
        name: 'AttendeesDetail',
        component: () => import('@/views/Attendees/AttendeesDetail.vue')
      },
      // {
      //   path: 'detail/:id/top-database-matches',
      //   name: 'AttendeeTopDatabaseMatches',
      //   component: () =>
      //     import('@/views/Attendees/AttendeeTopDatabaseMatches.vue')
      // },
      {
        path: 'detail/:id/AttendeesDatabaseMatch', // ⬅️ NEW
        name: 'AttendeesDatabaseMatch',
        component: () => import('@/views/Attendees/AttendeesDatabaseMatch.vue')
      }
    ]
  },
  // Events (registration)
  {
    path: '/event/form/:id',
    name: 'EventForm',
    meta: { title: 'Cindomatch: Registration', isPublic: true },
    component: () => import('@/views/Events/EventForm.vue')
  },
  // Clients (protected)
  {
    path: '/clients',
    component: () => import('@/layout/AdminLayout/index.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'Amarel: Clients'
    },
    children: [
      {
        path: '',
        name: 'Clients',
        component: () => import('@/views/Clients/ClientsList.vue')
      },
      {
        path: 'client/:id?',
        name: 'ClientsClient',
        component: () => import('@/views/Clients/ClientsNew.vue')
      }
    ]
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue')
  },
  {
    path: '/thanks',
    name: 'Thanks',
    meta: { title: 'Amarel: Registration', isPublic: true },
    component: () => import('@/views/FormFilledView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// auth guard stays the same
function getCurrentUserOnce() {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub()
      resolve(user || null)
    })
  })
}

router.beforeEach(async (to) => {
  document.title = to.meta.title || 'Amarel'
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isPublic = to.matched.some((record) => record.meta.isPublic)
  if (!requiresAuth || isPublic) return true
  const user = await getCurrentUserOnce()
  if (user) return true
  return { name: 'login', query: { redirect: to.fullPath } }
})

router.beforeEach(async (to) => {
  const requiresSuperAdmin = to.matched.some(
    (record) => record.meta.requiresSuperAdmin
  )

  if (!requiresSuperAdmin) return true

  const { ensureAdminProfileLoaded, isSuperAdmin } = useCurrentAdmin()
  await ensureAdminProfileLoaded()

  if (isSuperAdmin.value) return true

  return { name: 'forbidden' }
})

export default router
